import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ImportRecord, Vehicle } from '@msc-stock/shared';
import { api } from '../lib/api.js';
import { formatDate, useI18n, yen } from '../i18n.js';
import { Card, DivisionBadge, Empty, Notice } from '../components/ui.js';

/** 取り込み履歴。1 行を開くと、その回に入庫・売却になった車を出す。 */
export function ImportHistoryPage() {
  const { t, lang } = useI18n();
  const [imports, setImports] = useState<ImportRecord[] | null>(null);
  const [openId, setOpenId] = useState<number | null>(null);
  const [detail, setDetail] = useState<{ added: Vehicle[]; sold: Vehicle[] } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .imports()
      .then((r) => setImports(r.imports))
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  useEffect(() => {
    if (openId == null) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    api
      .importDetail(openId)
      .then((r) => !cancelled && setDetail({ added: r.added, sold: r.sold }))
      .catch(() => !cancelled && setDetail(null));
    return () => {
      cancelled = true;
    };
  }, [openId]);

  if (error) return <Notice kind="bad">{error}</Notice>;
  if (!imports) return <Empty>{t('loading')}</Empty>;

  return (
    <>
      <h1 className="page-title">{t('history')}</h1>
      <p className="page-sub">
        {imports.length === 0 ? <Link to="/import">{t('uploadTitle')} →</Link> : `${imports.length}`}
      </p>

      <div className="table-wrap">
        {imports.length === 0 ? (
          <Empty>{t('noResults')}</Empty>
        ) : (
          <table>
            <thead>
              <tr>
                <th>{t('snapshotDate')}</th>
                <th>{t('filename')}</th>
                <th className="num">{t('added')}</th>
                <th className="num">{t('soldDiff')}</th>
                <th className="num">{t('returned')}</th>
                <th className="num">{t('updated')}</th>
                <th className="num">{t('unchanged')}</th>
                <th>{t('uploadedAt')}</th>
                <th>{t('note')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {imports.map((im) => (
                <tr key={im.id}>
                  <td>{formatDate(im.snapshotDate, lang)}</td>
                  <td className="wrap">{im.filename}</td>
                  <td className="num">{im.addedCount}</td>
                  <td className="num" style={{ color: im.soldCount > 0 ? 'var(--bad)' : undefined }}>
                    {im.soldCount}
                  </td>
                  <td className="num">{im.returnedCount}</td>
                  <td className="num">{im.updatedCount}</td>
                  <td className="num muted">{im.unchangedCount}</td>
                  <td className="muted">{new Date(im.uploadedAt).toLocaleString(lang === 'ja' ? 'ja-JP' : 'en-GB')}</td>
                  <td className="wrap muted">{im.note || '—'}</td>
                  <td>
                    <button
                      type="button"
                      className="btn small"
                      onClick={() => setOpenId(openId === im.id ? null : im.id)}
                    >
                      {openId === im.id ? t('cancel') : t('detail')}
                    </button>{' '}
                    {im.r2Key && (
                      <a className="btn small" href={`/api/imports/${im.id}/file`}>
                        {t('download')}
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {openId != null && (
        <div className="grid cols-2" style={{ marginTop: 16 }}>
          <Card title={`${t('added')} (${detail?.added.length ?? 0})`}>
            <VehicleMiniList vehicles={detail?.added ?? []} />
          </Card>
          <Card title={`${t('soldDiff')} (${detail?.sold.length ?? 0})`}>
            <VehicleMiniList vehicles={detail?.sold ?? []} />
          </Card>
        </div>
      )}
    </>
  );
}

function VehicleMiniList({ vehicles }: { vehicles: Vehicle[] }) {
  const { t } = useI18n();
  if (vehicles.length === 0) return <div className="faint">—</div>;
  return (
    <div className="table-wrap" style={{ border: 'none' }}>
      <table>
        <thead>
          <tr>
            <th>{t('division')}</th>
            <th>{t('model')}</th>
            <th>{t('chassis')}</th>
            <th className="num">{t('purchasePrice')}</th>
          </tr>
        </thead>
        <tbody>
          {vehicles.map((v) => (
            <tr key={v.id}>
              <td>
                <DivisionBadge division={v.division} />
              </td>
              <td>
                <Link to={`/vehicles/${v.id}`}>
                  {v.maker} {v.model}
                </Link>
              </td>
              <td className="mono">{v.chassisRaw || '—'}</td>
              <td className="num">{yen(v.purchasePrice)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
